import { ModelRequest } from '@fixtuur/backpack';
import * as Brenda from '@fixtuur/brenda';
import { SceneButton } from '@fixtuur/brenda/src/renderer/sceneButtons';
import { useTheme } from '@mui/system';
import { Effect, pipe } from 'effect';
import * as O from 'fp-ts/Option';
import * as TO from 'fp-ts/TaskOption';
import * as Hooks from 'preact/hooks';
import { user3dSceneInteractionAction } from '../../store/actions/app';
import * as Core from '../../store/core';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selections from '../../store/selections';
import * as Selectors from '../../store/selectors';

export type GltfBlob = TO.TaskOption<Blob>;
export type ModelJson = TO.TaskOption<ModelRequest.ModelRequest>;

// TODO: get from some sort of client config
const defaultOptions: Brenda.Options = {
    camera: {
        panToModelOnSelection: false,
    },
    interactionHints: {
        camera: {
            active: true,
            image: 'assets/camera-nudge-hint.svg',
        },
    },
    environment: {
        skybox: { texture: 'assets/studio.env' },
    },
    antialias: true,
    handleResize: true,
};

export type RendererCallbacks = {
    onFurnitureModuleClick?: (furnitureId: string, moduleIndex: number) => void;
    onSceneClick?: () => void;
};

export function useRenderer(
    renderable: Brenda.Renderer.Renderable | undefined,
    showDimensions: boolean,
    overrideDimensions?: { width: number; height: number; depth: number },
    callbacks: RendererCallbacks = {},
    sceneButtons: SceneButton[] = [],
    rendererOptions: Brenda.Options = defaultOptions,
) {
    const rendererRef = Hooks.useRef<Brenda.Renderer.Renderer>();
    const measurementUnit = useSelector(Selectors.Core.measurementUnit);

    const theme = useTheme();

    const dispatch = useDispatch();

    const [loading, setLoading] = Hooks.useState<boolean>(false);
    const latestRenderableRef = Hooks.useRef<Brenda.Renderer.Renderable | undefined>(undefined);
    const renderablePending =
        renderable !== latestRenderableRef.current || renderable === undefined;

    const renderer = rendererRef.current;

    if (renderer && renderable && renderable !== latestRenderableRef.current && !loading) {
        latestRenderableRef.current = renderable;
        setLoading(true);
        Brenda.Renderer.render(
            renderer,
            renderable,
            showDimensions,
            theme.palette.primary.main,
            measurementUnit,
            overrideDimensions,
        );
    }

    Hooks.useEffect(
        () => () => {
            if (rendererRef.current) {
                Brenda.Renderer.shutDown(rendererRef.current);
            }
        },
        [],
    );

    const onRendererReady = (renderer: Brenda.Renderer.Renderer) => {
        if (rendererRef.current === undefined) {
            rendererRef.current = renderer;
        }
    };

    // Catch and direct errors appropriately depending on the running mode of the app
    const [pendingError, setPendingError] = Hooks.useState<string | null>(null);

    const renderableType = Hooks.useMemo(() => {
        return renderable?._tag === 'Product' ? 'model' : 'render';
    }, [renderable]);

    Hooks.useEffect(() => {
        if (pendingError !== null && renderableType) {
            dispatch(
                Core.error({
                    message: pendingError,
                    context: {
                        type: renderableType,
                    },
                }),
            );
            setPendingError(null);
        }
    }, [pendingError, renderableType]);

    const handleUserSceneInteraction = () => {
        dispatch(user3dSceneInteractionAction());
    };

    const handleSelectAttachmentOption = (proposal: Record<string, string>) => {
        dispatch(Selections.setAttachmentProposals(proposal));
    };

    const callbacksRef = Hooks.useRef<RendererCallbacks>({});
    callbacksRef.current = callbacks;

    Hooks.useEffect(() => {
        if (rendererRef.current) {
            Brenda.Renderer.onError(rendererRef.current, error => {
                setPendingError(error.reason);
            });
            Brenda.Renderer.onLoadSuccess(rendererRef.current, () => {
                dispatch(Core.clearError());
                setLoading(false);
            });
            Brenda.Renderer.onSceneUserInteraction(rendererRef.current, handleUserSceneInteraction);
            Brenda.Renderer.onSelectAttachmentOption(
                rendererRef.current,
                handleSelectAttachmentOption,
            );
            Brenda.Renderer.onFurnitureModuleClick(
                rendererRef.current,
                (furnitureId, moduleIdx) => {
                    callbacksRef.current.onFurnitureModuleClick?.(furnitureId, moduleIdx);
                },
            );
            Brenda.Renderer.onSceneClick(rendererRef.current, () => {
                callbacksRef.current.onSceneClick?.();
            });
        }
        return () => {
            if (rendererRef.current) {
                Brenda.Renderer.offError(rendererRef.current, () => {
                    dispatch(Core.clearError());
                });
                Brenda.Renderer.offSceneUserInteraction(
                    rendererRef.current,
                    handleUserSceneInteraction,
                );
            }
        };
    }, [rendererRef.current]);

    Hooks.useEffect(() => {
        if (rendererRef.current) {
            Brenda.Renderer.updateDimensions(
                rendererRef.current,
                showDimensions,
                theme.palette.primary.main,
                measurementUnit,
            );
        }
    }, [rendererRef.current, showDimensions]);

    Hooks.useEffect(() => {
        if (rendererRef.current) {
            Brenda.Renderer.SceneButtons.renderSceneButtons(
                rendererRef.current,
                sceneButtons,
                theme.palette.primary.main,
            );
        }
    }, [rendererRef.current, sceneButtons]);

    const gltfBlob = Hooks.useCallback(() => {
        const renderer = rendererRef.current;
        return (renderer ? TO.tryCatch(() => Brenda.Renderer.generateGltf(renderer)) : TO.none)();
    }, [rendererRef.current]);

    const modelJson = Hooks.useCallback(async () => {
        const renderer = rendererRef.current;
        if (!renderer) {
            return O.none;
        }
        const isModularFurniture = Brenda.Renderer.ModularFurniture.isModularFurniture(renderable);
        return O.some(
            isModularFurniture
                ? Brenda.Renderer.generateModularModelJson(renderer)
                : Brenda.Renderer.generateModelJson(renderer),
        );
    }, [renderable, rendererRef.current]);

    const modelGeneration = Hooks.useMemo(() => ({ gltfBlob, modelJson }), [gltfBlob, modelJson]);

    const takeSnapshot = Hooks.useMemo(
        () =>
            pipe(
                Effect.fromNullable(rendererRef.current),
                Effect.andThen(renderer =>
                    pipe(
                        Effect.tryPromise(() =>
                            Brenda.Renderer.takeSnapshot(
                                showDimensions,
                                measurementUnit,
                                theme.palette.primary.main,
                                overrideDimensions,
                            )(renderer)(),
                        ),
                        Effect.map(O.toNullable),
                        Effect.andThen(Effect.fromNullable),
                    ),
                ),
            ),
        [rendererRef.current, renderable, showDimensions, overrideDimensions],
    );

    const downloadConfigurationGlb = (fileName = 'export', partToExclude?: string) =>
        pipe(
            Effect.fromNullable(rendererRef.current),
            Effect.andThen(renderer =>
                pipe(
                    Effect.tryPromise(() => Brenda.Renderer.generateGltf(renderer, true, fileName, partToExclude)),
                ),
            ),
        );

    return {
        onRendererReady,
        rendererOptions,
        modelGeneration,
        takeSnapshot,
        loading: loading || renderablePending,
        rendererRef,
        downloadConfigurationGlb,
    };
}
